import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Store } from 'lucide-react';
import { formatCurrency, formatDateTime, shopifyAdminOrderUrl } from '@/lib/utils';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { FilterChips } from '@/components/ui/filter-chips';
import { Button } from '@/components/ui/button';
import { FinancialStatusBadge, FulfillmentStatusBadge } from '@/components/StatusBadge';
import { MobileMetric } from '@/components/MobileMetric';
import { useOrders } from '@/hooks/useOrders';
import { usePagination } from '@/hooks/usePagination';
import type { Order } from '@/types';

/** Best-effort customer display name from the raw Shopify order payload. */
function customerName(order: Order): string {
  const c = order.raw_data?.customer as
    | { first_name?: string | null; last_name?: string | null }
    | undefined;
  const name = [c?.first_name, c?.last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (order.customer_email) return order.customer_email;
  return '—';
}

// Status pills (FinancialStatusBadge / FulfillmentStatusBadge) are shared
// components in src/components/StatusBadge — one color mapping for the whole app.

const columns: DataTableColumn<Order>[] = [
  {
    key: 'name',
    label: 'Order',
    sortable: true,
    className: 'w-[90px]',
    render: (item) => (
      <span className="text-sm font-medium text-foreground">
        {item.name ?? `#${item.order_number}`}
      </span>
    ),
  },
  {
    key: 'shopify_created_at',
    label: 'Date',
    sortable: true,
    className: 'w-[170px]',
    render: (item) => (
      <span className="text-xs text-muted-foreground">
        {formatDateTime(item.shopify_created_at)}
      </span>
    ),
  },
  {
    key: 'customer',
    label: 'Customer',
    className: 'w-[220px]',
    render: (item) => (
      <span className="text-sm text-foreground truncate">{customerName(item)}</span>
    ),
  },
  {
    key: 'total_price',
    label: 'Total',
    sortable: true,
    className: 'w-[100px]',
    render: (item) => (
      <span className="text-sm font-mono text-foreground">{formatCurrency(item.total_price)}</span>
    ),
  },
  {
    key: 'financial_status',
    label: 'Payment',
    sortable: true,
    className: 'w-[100px]',
    render: (item) => (
      <FinancialStatusBadge status={item.financial_status} className="text-[10px]" />
    ),
  },
  {
    key: 'fulfillment_status',
    label: 'Fulfillment',
    sortable: true,
    className: 'w-[110px]',
    render: (item) => (
      <FulfillmentStatusBadge status={item.fulfillment_status} className="text-[10px]" />
    ),
  },
  {
    key: 'link',
    label: 'Shopify',
    className: 'w-[70px]',
    render: (item) => (
      <a
        href={shopifyAdminOrderUrl(item.shopify_order_id)}
        target="_blank"
        rel="noopener noreferrer"
        title="View order in Shopify admin"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-primary md:h-auto md:w-auto"
      >
        <Store className="h-3.5 w-3.5" />
      </a>
    ),
  },
];

function OrderMobileCard({ item }: { item: Order }) {
  return (
    <div className="space-y-3">
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {item.name ?? `#${item.order_number}`}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {customerName(item)}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {formatDateTime(item.shopify_created_at)}
          </p>
        </div>
        <a
          data-no-row-click
          href={shopifyAdminOrderUrl(item.shopify_order_id)}
          target="_blank"
          rel="noopener noreferrer"
          title="View order in Shopify admin"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
        >
          <Store className="h-4 w-4" />
        </a>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MobileMetric label="Total">
          <span className="text-sm font-mono text-foreground">
            {formatCurrency(item.total_price)}
          </span>
        </MobileMetric>
        <MobileMetric label="Payment">
          <FinancialStatusBadge status={item.financial_status} className="text-[10px]" />
        </MobileMetric>
        <MobileMetric label="Fulfillment">
          <FulfillmentStatusBadge status={item.fulfillment_status} className="text-[10px]" />
        </MobileMetric>
      </div>
    </div>
  );
}

const PAYMENT_CHIPS = [
  { value: 'all', label: 'All payments' },
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'refunded', label: 'Refunded' },
];

const FULFILLMENT_CHIPS = [
  { value: 'all', label: 'All fulfillment' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'unfulfilled', label: 'Unfulfilled' },
];

export function OrdersPage() {
  const navigate = useNavigate();
  const {
    page,
    perPage,
    search,
    sort,
    setPage,
    setSearch,
    setSort,
    getSearchPath,
  } = usePagination({
    initialSort: { field: 'shopify_created_at', direction: 'desc' },
  });

  const [paymentFilter, setPaymentFilter] = useState('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState('all');

  const { data, isLoading, isFetching } = useOrders({
    page,
    per_page: perPage,
    search,
    sort_by: sort.field,
    sort_dir: sort.direction,
    financial_status: paymentFilter !== 'all' ? paymentFilter : undefined,
    fulfillment_status: fulfillmentFilter !== 'all' ? fulfillmentFilter : undefined,
  });

  const handleRowClick = useCallback(
    (item: Order) => {
      navigate(getSearchPath(`/orders/${item.shopify_order_id}`));
    },
    [getSearchPath, navigate],
  );

  const filtersActive = paymentFilter !== 'all' || fulfillmentFilter !== 'all';

  const handlePaymentFilter = useCallback(
    (value: string) => {
      setPaymentFilter(value);
      setPage(1);
    },
    [setPage],
  );

  const handleFulfillmentFilter = useCallback(
    (value: string) => {
      setFulfillmentFilter(value);
      setPage(1);
    },
    [setPage],
  );

  const filterBar = (
    <div className="flex min-w-0 flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2">
      <FilterChips
        options={PAYMENT_CHIPS}
        value={paymentFilter}
        onChange={handlePaymentFilter}
        className="w-full md:w-auto"
      />
      <FilterChips
        options={FULFILLMENT_CHIPS}
        value={fulfillmentFilter}
        onChange={handleFulfillmentFilter}
        className="w-full md:w-auto"
      />
      {filtersActive && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 justify-center text-xs md:h-7"
          onClick={() => {
            setPaymentFilter('all');
            setFulfillmentFilter('all');
            setPage(1);
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.total ?? 0}
        page={page}
        perPage={perPage}
        sort={sort}
        search={search}
        isLoading={isLoading}
        isFetching={isFetching}
        getRowId={(item) => item.id}
        onPageChange={setPage}
        onSortChange={setSort}
        onSearchChange={setSearch}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by order number, customer email..."
        emptyMessage="No orders found"
        filterBar={filterBar}
        mobileRender={(item) => <OrderMobileCard item={item} />}
      />
    </div>
  );
}
